import type * as maplibregl from "maplibre-gl";
import { HILLSHADE_SOURCE, LANDCOVER_SOURCE, MAP_FONT, PLACES_SOURCE } from "./constants";
import { buildKmGrid } from "./gaussKruger";

// Слои, которых нет в нашем стиле и которые нужны только «Генштабу»:
// гипсометрическая раскраска высот, условные знаки леса и кустарника и
// километровая сетка. Добавляются один раз, скрытыми; setActive включает и
// выключает их вместе с выбором стиля (см. mapTheme.ts::GENSHTAB для
// палитры остальных слоёв).

const GRID_SOURCE = "gs-grid";
export const GS_CLIFF_IMAGE_ID = "gs-cliff-hachure";

// Знаки растительности зависят от масштаба, как на листах разных масштабов
// («Условные знаки», с. 36-38): на крупном плане - крупные знаки породы
// (без цифр высоты/толщины: в данных их нет), на среднем - знаки
// поменьше и гуще, на мелком - редкие мелкие значки. Данных о
// породе в слое landcover нет (leaf_type размечен у ~15% лесов и в тайл не
// попадает), поэтому, пока породы нет в данных, весь лес рисуется хвойным (ель/сосна) -
// такой выбор по умолчанию. Лиственный знак (broadleaf) оставлен для случая,
// когда leaf_type попадёт в тайл.
const FOREST_TIERS = [
  { id: "gs-forest-far", image: "gs-forest-far", minzoom: 11.5, maxzoom: 13 },
  { id: "gs-forest-mid", image: "gs-forest-mid", minzoom: 13, maxzoom: 15.5 },
  { id: "gs-forest-near", image: "gs-forest-near", minzoom: 15.5, maxzoom: 24 },
] as const;
const SCRUB_TIERS = [
  { id: "gs-scrub-far", image: "gs-scrub-far", minzoom: 12, maxzoom: 14.5 },
  { id: "gs-scrub-near", image: "gs-scrub-near", minzoom: 14.5, maxzoom: 24 },
] as const;
// Осыпи и курумники - красные точки (с. 42), фирн и ледники - голубые (с. 35).
const GROUND_PATTERNS = [
  { id: "gs-scree-pattern", image: "gs-scree", kind: "scree", opacity: 0.9 },
  { id: "gs-block-pattern", image: "gs-block", kind: "blockfield", opacity: 0.95 },
  { id: "gs-glacier-pattern", image: "gs-glacier", kind: "glacier", opacity: 0.9 },
] as const;

const LAYERS = [
  "gs-hypsometric",
  ...FOREST_TIERS.map((t) => t.id),
  ...SCRUB_TIERS.map((t) => t.id),
  ...GROUND_PATTERNS.map((t) => t.id),
  "gs-place-circle",
  "gs-place-dot",
  "gs-grid-line",
  "gs-grid-label",
];

// Ступени высот, м -> цвет: от зелёных низин через охру к коричневым
// нагорьям и бледным вершинам, как на мелкомасштабных листах серии.
const HYPSOMETRIC_STOPS: [number, string][] = [
  [400, "#cfe2b4"],
  [1000, "#e1e8b8"],
  [1500, "#f0e6b4"],
  [2000, "#ead3a0"],
  [2500, "#dfb887"],
  [3000, "#cf996d"],
  [3500, "#bc805b"],
  [4000, "#a96d51"],
  [5000, "#eae0d6"],
];

const GREEN = "#4f6f20";
const RED = "#c8422a";
const BLUE = "#3a82c4";

const drawImage = (w: number, h: number, paint: (ctx: CanvasRenderingContext2D) => void): ImageData | null => {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  paint(ctx);
  return ctx.getImageData(0, 0, w, h);
};

// Хвойное: ствол с тремя парами ветвей (ель, с. 36). height - в пикселях картинки.
const fir = (ctx: CanvasRenderingContext2D, cx: number, top: number, height: number) => {
  ctx.beginPath();
  ctx.moveTo(cx, top);
  ctx.lineTo(cx, top + height);
  for (let i = 0; i < 3; i++) {
    const y = top + height * (0.22 + i * 0.26);
    const w = height * (0.2 + i * 0.07);
    ctx.moveTo(cx - w, y + height * 0.14);
    ctx.lineTo(cx, y);
    ctx.lineTo(cx + w, y + height * 0.14);
  }
  ctx.stroke();
};

// Лиственное: круглая крона на ножке.
const broadleaf = (ctx: CanvasRenderingContext2D, cx: number, top: number, height: number) => {
  const r = height * 0.32;
  ctx.beginPath();
  ctx.arc(cx, top + r, r, 0, Math.PI * 2);
  ctx.moveTo(cx, top + 2 * r);
  ctx.lineTo(cx, top + height);
  ctx.stroke();
};

const style = (ctx: CanvasRenderingContext2D, color: string, width: number) => {
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = "round";
};

const ring = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number) => {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.stroke();
};

const dots = (ctx: CanvasRenderingContext2D, color: string, radius: number, points: [number, number][]) => {
  ctx.fillStyle = color;
  for (const [x, y] of points) {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
};

// Условные знаки точечных объектов (с. 12, 29, 30, 34): пункт геодезической
// сети - треугольник с точкой (вершины), родник - точка с хвостиком, колодец -
// кружок с точкой (оба синие), вход в пещеру - арка, вышка - рамка с мачтой.
// Рисуются 32x32 при pixelRatio 2 (16 css px), цвет запечён: SDF не нужен.
const INK = "#1c1a18";
const WATER = "#2b7fc2";
const poiGlyph = (paint: (ctx: CanvasRenderingContext2D) => void) => drawImage(32, 32, paint);

const buildPoiImages = (): Record<string, ImageData | null> => ({
  "gs-peak": poiGlyph((ctx) => {
    style(ctx, INK, 2);
    ctx.beginPath();
    ctx.moveTo(16, 4);
    ctx.lineTo(28, 26);
    ctx.lineTo(4, 26);
    ctx.closePath();
    ctx.stroke();
    dots(ctx, INK, 2.2, [[16, 19]]);
  }),
  "gs-spring": poiGlyph((ctx) => {
    style(ctx, WATER, 2.2);
    dots(ctx, WATER, 4, [[17, 11]]);
    ctx.beginPath();
    ctx.moveTo(15, 14);
    ctx.quadraticCurveTo(10, 20, 14, 27);
    ctx.stroke();
  }),
  "gs-well": poiGlyph((ctx) => {
    style(ctx, WATER, 2.4);
    ring(ctx, 16, 16, 8);
    dots(ctx, WATER, 2.6, [[16, 16]]);
  }),
  "gs-cave": poiGlyph((ctx) => {
    style(ctx, INK, 2.6);
    ctx.beginPath();
    ctx.moveTo(5, 26);
    ctx.lineTo(5, 18);
    ctx.arc(16, 18, 11, Math.PI, 0);
    ctx.lineTo(27, 26);
    ctx.stroke();
  }),
  "gs-tower": poiGlyph((ctx) => {
    style(ctx, INK, 2.2);
    ctx.beginPath();
    ctx.moveTo(9, 27);
    ctx.lineTo(14, 8);
    ctx.lineTo(18, 8);
    ctx.lineTo(23, 27);
    ctx.moveTo(11, 20);
    ctx.lineTo(21, 20);
    ctx.stroke();
    dots(ctx, INK, 2.4, [[16, 5]]);
  }),
});

const buildImages = (): Record<string, ImageData | null> => ({
  ...buildPoiImages(),
  "gs-forest-far": drawImage(48, 48, (ctx) => {
    style(ctx, GREEN, 1.2);
    fir(ctx, 12, 6, 13);
    fir(ctx, 34, 28, 13);
  }),
  "gs-forest-mid": drawImage(64, 64, (ctx) => {
    style(ctx, GREEN, 1.5);
    fir(ctx, 16, 8, 20);
    fir(ctx, 46, 36, 20);
  }),
  // Крупный план: тот же знак крупнее и реже. Характеристику древостоя
  // («высота / толщина», с. 37) не рисуем: в данных её нет, цифры были бы
  // выдуманными.
  "gs-forest-near": drawImage(136, 136, (ctx) => {
    style(ctx, GREEN, 1.8);
    fir(ctx, 34, 20, 38);
    fir(ctx, 98, 78, 38);
  }),
  // Кустарник (с. 38, «сплошные заросли»): россыпь мелких колечек и точек
  // вперемешку, без правильной сетки. Размер и густота растут с масштабом.
  "gs-scrub-far": drawImage(48, 48, (ctx) => {
    style(ctx, GREEN, 0.9);
    ring(ctx, 10, 12, 1.8);
    ring(ctx, 33, 30, 1.8);
    dots(ctx, GREEN, 0.7, [[26, 8], [8, 36], [41, 14], [20, 26]]);
  }),
  "gs-scrub-near": drawImage(72, 72, (ctx) => {
    style(ctx, GREEN, 1.1);
    ring(ctx, 12, 14, 2.8);
    ring(ctx, 44, 10, 2.8);
    ring(ctx, 30, 40, 2.8);
    ring(ctx, 60, 52, 2.8);
    ring(ctx, 10, 60, 2.8);
    dots(ctx, GREEN, 0.9, [[28, 14], [58, 28], [18, 34], [48, 64], [66, 8], [36, 62], [4, 44], [52, 40]]);
  }),
  "gs-scree": drawImage(48, 48, (ctx) => dots(ctx, RED, 1.3, [[6, 8], [24, 4], [40, 14], [14, 28], [34, 36], [8, 44]])),
  "gs-block": drawImage(40, 40, (ctx) =>
    dots(ctx, RED, 1.5, [[4, 5], [18, 3], [32, 8], [10, 17], [26, 20], [38, 28], [6, 32], [20, 36], [33, 38]]),
  ),
  "gs-glacier": drawImage(40, 40, (ctx) => dots(ctx, BLUE, 1.2, [[6, 6], [24, 10], [14, 22], [34, 26], [8, 34], [28, 36]])),
  // Скалы: штриховка тем же шагом, что у нашего стиля (13x15), но красно-коричневая.
  [GS_CLIFF_IMAGE_ID]: drawImage(13, 15, (ctx) => {
    ctx.fillStyle = "#b8421c";
    ctx.fillRect(0, 0, 13, 1.8);
    ctx.beginPath();
    ctx.moveTo(1.5, 1.8);
    ctx.lineTo(11.5, 1.8);
    ctx.lineTo(6.5, 13);
    ctx.closePath();
    ctx.fill();
  }),
});

const refreshGrid = (m: maplibregl.Map) => {
  const src = m.getSource(GRID_SOURCE) as maplibregl.GeoJSONSource | undefined;
  if (!src) return;
  const b = m.getBounds();
  src.setData(buildKmGrid({ west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth() }, m.getZoom()));
};

export interface GenshtabExtras {
  setActive: (on: boolean) => void;
}

export const installGenshtabExtras = (m: maplibregl.Map): GenshtabExtras => {
  for (const [id, img] of Object.entries(buildImages())) {
    if (img && !m.hasImage(id)) m.addImage(id, img, { pixelRatio: id === GS_CLIFF_IMAGE_ID ? 1 : 2 });
  }
  m.addSource(GRID_SOURCE, { type: "geojson", data: { type: "FeatureCollection", features: [] } });

  const hidden = { visibility: "none" as const };
  // Раскраска высот - под покровом земли: лес и вода рисуются поверх неё
  // полупрозрачно (см. fill-opacity в GENSHTAB), и тон высоты просвечивает.
  m.addLayer(
    {
      id: "gs-hypsometric",
      type: "color-relief",
      source: HILLSHADE_SOURCE,
      layout: hidden,
      paint: {
        "color-relief-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0.75, 13, 0.55, 15.5, 0.2] as never,
        "color-relief-color": ["interpolate", ["linear"], ["elevation"], ...HYPSOMETRIC_STOPS.flat()] as never,
      },
    },
    "landcover-ground",
  );
  const landcoverFill = (id: string, image: string, kind: string, opacity: number, minzoom: number, maxzoom: number) =>
    m.addLayer(
      {
        id,
        type: "fill",
        source: LANDCOVER_SOURCE,
        "source-layer": LANDCOVER_SOURCE,
        minzoom,
        maxzoom,
        filter: ["==", ["get", "kind"], kind],
        layout: hidden,
        paint: { "fill-pattern": image, "fill-opacity": opacity },
      },
      "hillshade",
    );
  for (const t of FOREST_TIERS) landcoverFill(t.id, t.image, "forest", 0.85, t.minzoom, t.maxzoom);
  for (const t of SCRUB_TIERS) landcoverFill(t.id, t.image, "scrub", 0.85, t.minzoom, t.maxzoom);
  for (const g of GROUND_PATTERNS) landcoverFill(g.id, g.image, g.kind, g.opacity, 11, 24);
  // Пунсоны населённых пунктов (с. 44): город от 50 тыс. - кружок с оранжевым
  // заполнением и точкой, меньше - белый с точкой, село - пустое колечко.
  m.addLayer({
    id: "gs-place-circle",
    type: "circle",
    source: PLACES_SOURCE,
    "source-layer": PLACES_SOURCE,
    minzoom: 8,
    maxzoom: 12,
    filter: ["match", ["get", "kind"], ["city", "town", "village"], true, false],
    layout: hidden,
    paint: {
      "circle-radius": [
        "case",
        [">=", ["coalesce", ["get", "population"], 0], 50000], 5.5,
        ["==", ["get", "kind"], "village"], 2.6,
        3.8,
      ],
      "circle-color": ["case", [">=", ["coalesce", ["get", "population"], 0], 50000], "#6b5f58", "#fbf8f0"],
      "circle-stroke-color": "#1c1a18",
      "circle-stroke-width": 1,
    },
  });
  m.addLayer({
    id: "gs-place-dot",
    type: "circle",
    source: PLACES_SOURCE,
    "source-layer": PLACES_SOURCE,
    minzoom: 8,
    maxzoom: 12,
    filter: ["all", ["match", ["get", "kind"], ["city", "town"], true, false]],
    layout: hidden,
    paint: { "circle-radius": 1.2, "circle-color": "#1c1a18" },
  });
  // Сетка - над рельефом и водой, под дорогами и значками.
  m.addLayer(
    {
      id: "gs-grid-line",
      type: "line",
      source: GRID_SOURCE,
      layout: hidden,
      paint: { "line-color": "#1f3a5c", "line-width": 0.6, "line-opacity": 0.55 },
    },
    "contours-line",
  );
  m.addLayer(
    {
      id: "gs-grid-label",
      type: "symbol",
      source: GRID_SOURCE,
      layout: {
        ...hidden,
        "text-field": ["get", "label"],
        "text-font": MAP_FONT,
        "text-size": 10,
        "symbol-placement": "line",
        "symbol-spacing": 400,
        "text-keep-upright": true,
        "text-padding": 4,
      },
      paint: { "text-color": "#1f3a5c", "text-halo-color": "#fbf8f0", "text-halo-width": 1.2 },
    },
    "pois-label",
  );

  let active = false;
  const onMove = () => {
    if (active) refreshGrid(m);
  };
  m.on("moveend", onMove);

  return {
    setActive(on) {
      active = on;
      for (const id of LAYERS) {
        if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", on ? "visible" : "none");
      }
      if (on) refreshGrid(m);
    },
  };
};
